import { useCallback, useEffect, useRef, useState } from "react";

interface UseAuthSubmissionGuardOptions {
  hasAuth0Error: boolean;
  dismissAuth0Errors: () => void;
}

interface AuthSubmissionGuard {
  dismissErrors: () => void;
  hasSubmitRejection: boolean;
  isSubmitLocked: boolean;
  submit: (submission: () => Promise<unknown>) => Promise<void>;
}

/**
 * Owns the submission lifecycle shared by authentication forms.
 *
 * The lock is set synchronously to reject duplicate submissions and remains
 * active after a successful SDK call while Auth0 navigation is pending.
 */
export function useAuthSubmissionGuard({
  hasAuth0Error,
  dismissAuth0Errors,
}: UseAuthSubmissionGuardOptions): AuthSubmissionGuard {
  const submissionLockedRef = useRef(false);
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const [hasSubmitRejection, setHasSubmitRejection] = useState(false);

  const releaseSubmissionLock = useCallback(() => {
    submissionLockedRef.current = false;
    setIsSubmitLocked(false);
  }, []);

  useEffect(() => {
    if (hasAuth0Error) {
      releaseSubmissionLock();
    }
  }, [hasAuth0Error, releaseSubmissionLock]);

  const dismissErrors = useCallback(() => {
    if (hasAuth0Error) {
      dismissAuth0Errors();
    }
    setHasSubmitRejection(false);
  }, [dismissAuth0Errors, hasAuth0Error]);

  const submit = useCallback(
    async (submission: () => Promise<unknown>) => {
      if (submissionLockedRef.current) {
        return;
      }

      submissionLockedRef.current = true;
      setIsSubmitLocked(true);
      setHasSubmitRejection(false);

      try {
        await submission();
        if (hasAuth0Error) {
          releaseSubmissionLock();
        }
      } catch {
        // Never surface SDK rejection details — they can leak account hints.
        releaseSubmissionLock();
        setHasSubmitRejection(true);
      }
    },
    [hasAuth0Error, releaseSubmissionLock],
  );

  return {
    dismissErrors,
    hasSubmitRejection,
    isSubmitLocked,
    submit,
  };
}
